<script setup>
  import {onMounted, ref, watch} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import PatientinputMedicationRow from "@pages/Patientinput/Components/MedicationRow.vue";
  import PatientinputMedicationSearch from "@pages/Patientinput/Components/MedicationSearch.vue";
  import PatientinputComponentPznScannerDialog from "@pages/Patientinput/Components/Scanners/PznScannerDialog.vue";
  import PatientinputComponentStep from "@pages/Patientinput/Components/Step.vue";
  import {MedButton, MedSpinner} from "@pharma4u/medicheck-components";
  import {coreRouter} from "@utils/coreRouter/coreRouter.js";

  defineOptions({
    name: "PatientinputComponentStepMedication",
  });

  const props = defineProps({
    form: {
      type: Object,
      required: true,
    },
  });

  defineEmits(["navigate"]);

  const refConfirmDeleteDialog = ref(null);

  const loadingMedications = ref(false);
  const medicationToDelete = ref(null);

  onMounted(() => {
    if (props.form.medication.pzns.length > 0) {
      loadingMedications.value = true;

      coreRouter.searchPacForPatientinput(props.form.medication.pzns.join(","), {
        onSuccess: (result) => {
          props.form.medication.list = result.data;
          loadingMedications.value = false;
        },
      });
    }
  });

  watch(
    () => props.form.medication.pzns,
    (pzns) => {
      props.form.medication.list = props.form.medication.list.filter((medication) =>
        pzns.some((pzn) => Number.parseInt(medication.PZN) === Number.parseInt(pzn)),
      );

      if (props.form.medication.list.length !== pzns.length) {
        loadingMedications.value = true;

        coreRouter.searchPacForPatientinput(pzns.join(","), {
          onSuccess: (result) => {
            props.form.medication.list = result.data;
            loadingMedications.value = false;
          },
        });
      }
    },
    {deep: true},
  );

  const confirmDelete = (medication) => {
    medicationToDelete.value = medication;
    refConfirmDeleteDialog.value.open();
  };

  const deleteConfirmed = () => {
    props.form.medication.pzns = props.form.medication.pzns.filter(
      (pzn) => Number.parseInt(pzn) !== Number.parseInt(medicationToDelete.value.PZN),
    );
    props.form.medication.list = props.form.medication.list.filter(
      ({PZN}) => Number.parseInt(PZN) !== Number.parseInt(medicationToDelete.value.PZN),
    );
    medicationToDelete.value = null;
  };
</script>

<template>
  <PatientinputComponentStep title="Medikation" :test-id="getTestId()">
    <div class="flex flex-col gap-8">
      <section>
        <p v-if="form.meta.is_carer">
          Bitte geben Sie nun Medikamente, Nahrungsergänzungsmittel etc. des Patienten an.
        </p>
        <p v-else>Bitte geben Sie nun Ihre Medikamente, Nahrungsergänzungsmittel etc. an.</p>
      </section>

      <section>
        <p>Scannen Sie den jeweiligen Barcode der Packungen ein, indem Sie auf „Barcodes scannen“ klicken.</p>

        <MedButton
          full-width
          :data-test="getTestId('medication-step-barcode-scan-button')"
          variant="primary"
          @click="$refs.pznscanner.open()"
        >
          Barcodes scannen
        </MedButton>
      </section>

      <section>
        <p>Oder tippen Sie den Medikamentennamen oder die PZN in die folgende Zeile ein.</p>

        <PatientinputMedicationSearch :form="form" :test-id="getTestId('medication-step')" />
      </section>

      <section v-if="form.medication.pzns.length > 0">
        <h2 v-if="form.meta.is_carer" class="text-2xl">Medikamente</h2>
        <h2 v-else class="text-2xl">Ihre Medikamente</h2>

        <MedSpinner v-if="loadingMedications" size="xl" :data-test="getTestId('medication-step-spinner')" />

        <template v-else>
          <ComponentAlert
            v-if="form.medication.pzns.length !== form.medication.list.length"
            type="warning"
            class="mb-0!"
            :test-id="getTestId('medication-step')"
          >
            Es wurden nicht alle gescannten Medikamente bzw. Medikamente aus dem Medikationsplan gefunden.
          </ComponentAlert>

          <ul v-if="form.medication.list.length > 0" class="flex flex-col gap-2">
            <PatientinputMedicationRow
              v-for="(medication, index) in form.medication.list"
              :key="index"
              :medication="medication"
              :form="form"
              :test-id="getTestId(`medication-step${index}`)"
              @delete="confirmDelete"
            />
          </ul>
        </template>
      </section>
    </div>
  </PatientinputComponentStep>

  <PatientinputComponentPznScannerDialog ref="pznscanner" :form="form" :test-id="getTestId('medication-step')" />

  <ComponentDialog
    ref="refConfirmDeleteDialog"
    dialog-title="Medikament löschen?"
    primary-label="Ja, Löschen"
    :test-id="getTestId('medication-step-confirm')"
    @primary-button-clicked="deleteConfirmed"
  >
    <template #content>
      Sind Sie sicher, dass Sie das Medikament „{{ medicationToDelete.Name }}“ löschen möchten?
    </template>
  </ComponentDialog>
</template>

<style scoped>
  @reference "@css/app.css";

  section {
    @apply flex flex-col gap-3;
  }
</style>
